import { useEffect, useMemo, useState } from 'react';
import { useMarineAccess } from './AuthGate';
import { supabase } from './lib/supabase';
import './platform-support.css';

type Org = {id:string;name:string;organization_type:string};
type Member = {organization_id:string;user_id:string;role:string;active:boolean};
type Profile = {user_id:string;full_name:string|null;email:string|null};
type SupportUser = Member & {full_name:string|null;email:string|null};

export default function PlatformSupportPanel(){
  const access = useMarineAccess();
  const [open,setOpen] = useState(false);
  const [loading,setLoading] = useState(false);
  const [organizations,setOrganizations] = useState<Org[]>([]);
  const [members,setMembers] = useState<SupportUser[]>([]);
  const [selectedOrg,setSelectedOrg] = useState('');
  const [busyUser,setBusyUser] = useState('');
  const [message,setMessage] = useState('');
  const [error,setError] = useState('');

  useEffect(()=>{
    if (!open || !access.isPlatformAdmin || !supabase) return;
    let cancelled=false;
    async function load(){
      setLoading(true);setError('');
      const [orgResult,memberResult]=await Promise.all([
        supabase!.from('marine_organizations').select('id,name,organization_type').neq('organization_type','platform_internal').order('name'),
        supabase!.from('marine_organization_members').select('organization_id,user_id,role,active').eq('active',true),
      ]);
      if (cancelled) return;
      if (orgResult.error || memberResult.error){setError(orgResult.error?.message||memberResult.error?.message||'Unable to load tenant users.');setLoading(false);return;}
      const nextOrgs=(orgResult.data||[]) as Org[];
      const baseMembers=(memberResult.data||[]) as Member[];
      const ids=[...new Set(baseMembers.map((row)=>row.user_id))];
      let profiles:Profile[]=[];
      if(ids.length){
        const profileResult=await supabase!.from('marine_profiles').select('user_id,full_name,email').in('user_id',ids);
        if(!profileResult.error) profiles=(profileResult.data||[]) as Profile[];
      }
      if(cancelled) return;
      setOrganizations(nextOrgs);
      setMembers(baseMembers.map((member)=>({...member,...(profiles.find((profile)=>profile.user_id===member.user_id)||{full_name:null,email:null})})));
      setSelectedOrg((current)=>current||nextOrgs[0]?.id||'');
      setLoading(false);
    }
    load();
    return ()=>{cancelled=true};
  },[open,access.isPlatformAdmin]);

  const visible=useMemo(()=>members.filter((member)=>member.organization_id===selectedOrg),[members,selectedOrg]);
  if(!access.isPlatformAdmin) return null;

  async function sendReset(member:SupportUser){
    if(!supabase) return;
    setBusyUser(member.user_id);setMessage('');setError('');
    const {data,error:invokeError}=await supabase.functions.invoke('marine-send-password-reset',{body:{organization_id:member.organization_id,user_id:member.user_id}});
    setBusyUser('');
    if(invokeError||data?.error){setError(invokeError?.message||String(data?.error||'Unable to send reset email.'));return;}
    setMessage(`Password reset sent to ${data?.email||member.email||'tenant user'}.`);
  }

  return <>
    <button className="platform-support-launcher" onClick={()=>setOpen(true)}>Platform Support</button>
    {open&&<div className="platform-support-backdrop" onMouseDown={()=>setOpen(false)}>
      <section className="platform-support-panel" onMouseDown={(event)=>event.stopPropagation()}>
        <header><div><span>Aqua Operations</span><h2>Tenant Support</h2><p>Manage tenant login recovery without changing or viewing anyone's password.</p></div><button onClick={()=>setOpen(false)}>×</button></header>
        <label className="platform-support-org">Organization<select value={selectedOrg} onChange={(event)=>{setSelectedOrg(event.target.value);setMessage('');setError('')}}>{organizations.map((org)=><option key={org.id} value={org.id}>{org.name}</option>)}</select></label>
        {message&&<div className="auth-alert success">{message}</div>}{error&&<div className="auth-alert error">{error}</div>}
        {loading?<div className="platform-support-empty">Loading tenant users…</div>:visible.length?<div className="platform-support-users">{visible.map((member)=><div className="platform-support-user" key={`${member.organization_id}-${member.user_id}`}><div className="support-avatar">{(member.full_name||member.email||'U').slice(0,1).toUpperCase()}</div><div><strong>{member.full_name||'MarineOps user'}</strong><span>{member.email||member.user_id}</span><small>{member.role.replaceAll('_',' ')}</small></div><button disabled={busyUser===member.user_id||!member.email} onClick={()=>sendReset(member)}>{busyUser===member.user_id?'Sending…':'Send password reset'}</button></div>)}</div>:<div className="platform-support-empty">No active tenant users in this organization.</div>}
      </section>
    </div>}
  </>;
}
